import clsx from 'clsx'
import Avatar from 'react-avatar'
import PropTypes from 'prop-types'
import { useMemo, useState } from 'react'
import { connect } from 'react-redux'
import { toast } from 'react-toastify'
import { useTranslation } from 'react-i18next'

import http from '../../../../shared/http'
import { TApi, DOCUMENT_TYPES } from '../../../../shared/const'
import { types } from '../../../../shared/eventEmitter'
import { askConfirm } from '../../../../shared/confirmDialog'
import { optionLabel } from '../../../../shared/optionList'
import { fullLogOut, invitationValidity } from '../../../../shared/helpers'
import { useAvatarUrls } from '../../../../shared/useAvatarUrls'
import { updateProfile } from '../../../../redux/ducks/user'

import Btn from '../../../../UI/Btn'
import StatusIcon from '../../../../UI/StatusIcon'
import BaseModal from '../../../../UI/BaseModal/BaseModal'

import WorkArea from '../../../views/student/Profile/components/WorkArea'

import styles from './style.module.scss'
import { formatDate as formatDateInVienna } from '../../../../shared/datetime'

// Was das Backend beim Übertragen der Geschäftsführung als Hauptfunktion setzt
// (api/private/student/users/controller.js). Wir spiegeln es, damit das Fenster
// nach dem Wechsel sofort den richtigen Stand zeigt.
const CEO_WORK_AREA = 'CEO / Managing Director'

// Das Formular der Arbeitsbereiche wird von der Fussleiste des Fensters
// abgesendet (HTML-Attribut `form`), nicht von einem Knopf in seinem Inneren.
const WORK_AREA_FORM_ID = 'employee-work-area-form'

/**
 * Eine Mitarbeiter:in der eigenen Junior Company — geöffnet aus der Liste.
 *
 * Vorher öffnete ein Klick im Zeilenmenü nur die Arbeitsbereiche; Name,
 * E-Mail-Adresse und Status standen ausschließlich in der Tabelle. Hier liegt
 * beides zusammen: die Angaben zur Person und die beiden Dinge, die sich an ihr
 * ändern lassen — die Funktion (Arbeitsbereiche) und die Geschäftsführung.
 *
 * Eingeladene, die ihr Konto noch nicht angelegt haben, haben kein Profil.
 * Für sie zeigt das Fenster die Einladung selbst: an welche Adresse sie ging,
 * wie lange sie noch gilt, und die beiden Handlungen dazu.
 */
function EmployeeDetailModal(props) {
	const { t, i18n } = useTranslation()

	const [context, setContext] = useState(null)
	const [employee, setEmployee] = useState(null)
	const [isLoading, setLoading] = useState(false)

	const avatarUrls = useAvatarUrls(employee?.avatarHashName ? [employee.avatarHashName] : [])

	// Stabiler Wert: `WorkArea` hängt seinen Befüll-Lauf daran. Ein bei jedem
	// Rendern neu gebautes Objekt liesse ihn endlos wiederholen.
	const workAreaStudent = useMemo(
		() =>
			employee && !employee.isInvited
				? {
						studentId: employee.id,
						isCEO: Boolean(employee.isCEO),
						primaryWorkArea: employee.primaryWorkArea || '',
						secondaryWorkArea: employee.secondaryWorkArea || '',
						tertiaryWorkArea: employee.tertiaryWorkArea || ''
				  }
				: null,
		[employee]
	)

	const handleOpen = payload => {
		setContext(payload)
		setEmployee(payload.employee)
	}

	const handleClose = () => {
		setContext(null)
		setEmployee(null)
	}

	const refresh = () => {
		if (context?.refresh) context.refresh()
	}

	const isInvited = Boolean(employee?.isInvited)
	const isSelf = Boolean(employee && employee.id === props.userId)
	const canEdit = Boolean(context?.canEdit)

	const fullName = employee?.firstName ? `${employee.firstName} ${employee.lastName}` : employee?.email || '-------'

	const formatDate = value => formatDateInVienna(value)

	const inviterName = person => (person ? `${person.firstName} ${person.lastName}`.trim() : '')

	// Deutsch oder Englisch gespeichert — `optionLabel` löst beides auf.
	const workAreaLabel = value => optionLabel(props.workAreas, value, i18n.language)

	const makeCEO = async () => {
		const confirmed = await askConfirm({
			title: t('Assign managing director (CEO)'),
			text: t('The CEO function is transferred to {{name}}. The current CEO loses it.', { name: fullName }),
			confirmLabel: t('Assign managing director (CEO)'),
			cancelLabel: t('Cancel')
		})
		if (!confirmed) return

		setLoading(true)
		http.put(TApi.STUDENT_MAKE_CEO(employee.id))
			.then(() => {
				toast.success(t('CEO Updated'))
				// Die eigene Geschäftsführung ist damit abgegeben.
				if (props.isCEO) props.updateProfile({ isCEO: false })
				setEmployee(current => ({
					...current,
					isCEO: true,
					primaryWorkArea: CEO_WORK_AREA,
					secondaryWorkArea: current.primaryWorkArea,
					tertiaryWorkArea: current.secondaryWorkArea
				}))
				refresh()
			})
			.finally(() => setLoading(false))
	}

	const removeEmployee = async closeModal => {
		const confirmed = await askConfirm({
			title: t('Are you sure?'),
			text: t('Remove employee.'),
			confirmLabel: t('Remove'),
			cancelLabel: t('Cancel'),
			tone: 'danger'
		})
		if (!confirmed) return

		setLoading(true)
		http.delete(TApi.STUDENT_DELETE_EMPLOYEE(employee.id))
			.then(() => {
				toast.success(t('Employee successfully deleted'))
				refresh()
				closeModal()
			})
			.finally(() => setLoading(false))
	}

	const leaveCompany = async () => {
		const confirmed = await askConfirm({
			title: t('Leave company'),
			text: t('You will be removed from this Junior Company and logged out.'),
			confirmLabel: t('Leave company'),
			cancelLabel: t('Cancel'),
			tone: 'danger'
		})
		if (!confirmed) return

		setLoading(true)
		http.post(TApi.STUDENT_LEAVE_COMPANY)
			.then(() => fullLogOut())
			.finally(() => setLoading(false))
	}

	const resendInvitation = () => {
		setLoading(true)
		http.post(TApi.STUDENT_RESEND_INVITE(employee.id))
			.then(() => {
				toast.success(t('Invitation resent successfully'))
				setEmployee(current => ({ ...current, invitedAt: new Date().toISOString() }))
				refresh()
			})
			.finally(() => setLoading(false))
	}

	const deleteInvitation = async closeModal => {
		const confirmed = await askConfirm({
			title: t('Delete invitation'),
			text: t('The invitation to {{email}} will be withdrawn.', { email: employee.email }),
			confirmLabel: t('Delete invitation'),
			cancelLabel: t('Cancel'),
			tone: 'danger'
		})
		if (!confirmed) return

		setLoading(true)
		http.delete(TApi.STUDENT_DELETE_INVITE_EMPLOYEE(employee.id))
			.then(() => {
				toast.success(t('Invitation deleted'))
				refresh()
				closeModal()
			})
			.finally(() => setLoading(false))
	}

	const parentalConfirmation = () => {
		const document = employee.Documents?.find(({ type }) => type === DOCUMENT_TYPES.PARENTAL_CONFIRMATION)

		if (document && employee.rejectedParentalConfirmation)
			return { icon: 'rejected', label: t('Rejected'), tone: styles.toneBad }
		if (document && employee.reviewedParentalConfirmation)
			return { icon: 'check', label: t('Checked'), tone: styles.toneGood }
		if (document) return { icon: 'check', label: t('Submitted'), tone: styles.toneNeutral }
		return { icon: 'warning', label: t('Open'), tone: styles.toneWarn }
	}

	const renderField = (label, value) => (
		<div className={styles.field}>
			<span className={styles.fieldLabel}>{label}</span>
			<span className={styles.fieldValue}>{value || '—'}</span>
		</div>
	)

	const renderInvitation = closeModal => {
		const validity = invitationValidity(employee.invitedAt)

		return (
			<div className={styles.root}>
				<div className={styles.head}>
					<Avatar round size={56} name={employee.email} className={styles.avatar} />
					<div>
						<h3 className={styles.name}>{employee.email}</h3>
						<span className={`${styles.badge} ${styles.toneWarn}`}>
							<StatusIcon name="invited" size={16} />
							{t('Invited')}
						</span>
					</div>
				</div>

				{/*
				 * Wer eingeladen hat und wann — die erste Frage der Junior Company
				 * zu einer offenen Einladung. Vorher stand hier der Satz, eine
				 * zweite Einladung sei nicht nötig; der beantwortete sie nicht.
				 * Bei Einladungen von vor dem 08.08.2026 ist niemand vermerkt,
				 * dann bleibt es beim Datum.
				 */}
				<div className={styles.fields}>
					{renderField(t('E-Mail'), employee.email)}
					{/*
					 * „Eingeladen von" nur, wenn es jemanden dazu gibt. Ein
					 * Strich an dieser Stelle sähe aus, als wäre etwas verloren
					 * gegangen — bei alten Einladungen wurde es nie erfasst.
					 */}
					{!!employee.InvitedBy && renderField(t('Invited by'), inviterName(employee.InvitedBy))}
					{renderField(t('Invited on'), formatDate(employee.invitedAt))}
					{renderField(
						t('Validity'),
						!validity
							? null
							: validity.expired
							? t('Expired')
							: t('Valid for {{days}} more days', { days: validity.daysLeft })
					)}
				</div>

				{canEdit && (
					<div className={styles.actions}>
						<Btn theme="secondary" loading={isLoading} onClick={resendInvitation}>
							{t('Resend invitation')}
						</Btn>
						<Btn theme="danger" loading={isLoading} onClick={() => deleteInvitation(closeModal)}>
							{t('Delete invitation')}
						</Btn>
					</div>
				)}
			</div>
		)
	}

	const renderEmployee = closeModal => {
		const parental = parentalConfirmation()
		const otherAreas = [employee.secondaryWorkArea, employee.tertiaryWorkArea].filter(Boolean)

		return (
			<div className={styles.root}>
				<div className={styles.head}>
					<Avatar
						round
						size={56}
						name={fullName}
						src={avatarUrls[employee.avatarHashName] || ''}
						className={styles.avatar}
					/>
					<div>
						<h3 className={styles.name}>
							{fullName}
							{isSelf && <span className={styles.self}>({t('You')})</span>}
						</h3>
						<div className={styles.badges}>
							<span
								className={`${styles.badge} ${
									employee.emailConfirmed ? styles.toneGood : styles.toneWarn
								}`}
							>
								<StatusIcon name={employee.emailConfirmed ? 'check' : 'pending'} size={16} />
								{employee.emailConfirmed ? t('Active') : t('Verification')}
							</span>
							{employee.isCEO && (
								<span className={`${styles.badge} ${styles.toneNeutral}`}>
									{t('Managing director (CEO)')}
								</span>
							)}
						</div>
					</div>
				</div>

				<div className={styles.fields}>
					{renderField(t('Name'), fullName)}
					{renderField(t('E-Mail'), employee.email)}
					{renderField(t('phone'), employee.phone)}
					{renderField(
						t('Main function'),
						employee.isCEO ? t('Managing director (CEO)') : workAreaLabel(employee.primaryWorkArea)
					)}
					{renderField(t('Work area'), otherAreas.map(workAreaLabel).join(', '))}
					{renderField(
						t('Parental Confirmation'),
						<span className={`${styles.inlineStatus} ${parental.tone}`}>
							<StatusIcon name={parental.icon} size={16} />
							{parental.label}
						</span>
					)}
				</div>

				{/*
				 * Die Geschäftsführung liegt immer bei genau einer Person. Sie wird
				 * deshalb weitergegeben, nicht an- und abgeschaltet — wer sie
				 * übernimmt, nimmt sie der bisherigen Inhaberin ab.
				 */}
				{canEdit && !employee.isCEO && (
					<div className={styles.ceoBox}>
						<div className={styles.ceoIntro}>
							<strong className={styles.ceoTitle}>{t('Managing director (CEO)')}</strong>
							<p className={styles.ceoText}>
								{t(
									'The CEO role must always be assigned to one person. It can be assigned to another person via the 3-point menu in the "Employees" view. CEO name is shown on the company register extract.'
								)}
							</p>
						</div>
						<Btn theme="secondary" loading={isLoading} onClick={makeCEO}>
							{t('Assign managing director (CEO)')}
						</Btn>
					</div>
				)}

				{/*
				 * Funktion und Arbeitsbereiche kommen aus demselben Formular wie im
				 * eigenen Profil — eine Stelle, an der die Regeln (genau eine
				 * Hauptfunktion, höchstens zwei weitere) gepflegt werden.
				 */}
				<WorkArea
					hideSubmit
					formId={WORK_AREA_FORM_ID}
					isTeacherStudentView={!canEdit}
					refreshEmployees={refresh}
					modalStudent={workAreaStudent}
				/>

				{/*
				 * Eine Fussleiste für das ganze Fenster: links die Handlung, die
				 * die Person betrifft, rechts das Speichern des Formulars darüber
				 * (über `form`, deshalb steht es hier und nicht im Formular).
				 * Vorher hatte das Formular seinen eigenen Speichern-Knopf und
				 * darunter kam eine zweite Knopfreihe — nach dem Wechsel der
				 * Geschäftsführung standen so bis zu vier Knöpfe untereinander.
				 */}
				{canEdit && (
					<div className={clsx(styles.actions, styles.actionsSplit)}>
						{isSelf ? (
							<Btn
								theme="danger"
								loading={isLoading}
								disabled={!context?.canLeave}
								onClick={leaveCompany}
							>
								{t('Leave company')}
							</Btn>
						) : (
							<Btn
								theme="danger"
								loading={isLoading}
								disabled={context?.canRemove === false}
								onClick={() => removeEmployee(closeModal)}
							>
								{t('Delete employee')}
							</Btn>
						)}

						<Btn
							type="submit"
							form={WORK_AREA_FORM_ID}
							loading={isLoading}
							icon="arrow"
							iconSize={{ w: 10, h: 10 }}
							iconColor="#fff"
						>
							{t('save')}
						</Btn>
					</div>
				)}
				{isSelf && canEdit && !context?.canLeave && <p className={styles.hint}>{context?.leaveHint}</p>}
				{!isSelf && canEdit && context?.canRemove === false && (
					<p className={styles.hint}>{context?.removeHint}</p>
				)}
			</div>
		)
	}

	return (
		<BaseModal
			fitViewport
			maxWidth={720}
			onOpen={handleOpen}
			onClose={handleClose}
			isLoading={isLoading}
			nameEvent={types.openEmployeeDetail}
			title={isInvited ? t('Invitation') : t('Employee')}
		>
			{({ closeModal }) => {
				if (!employee) return null
				return isInvited ? renderInvitation(closeModal) : renderEmployee(closeModal)
			}}
		</BaseModal>
	)
}

EmployeeDetailModal.propTypes = {
	userId: PropTypes.number,
	isCEO: PropTypes.bool,
	workAreas: PropTypes.array,
	updateProfile: PropTypes.func.isRequired
}

const mapStateToProps = state => ({
	userId: state.user.profile.id,
	isCEO: state.user.profile.isCEO,
	workAreas: state.options.workAreas
})

const mapDispatchToProps = dispatch => ({
	updateProfile: data => dispatch(updateProfile(data))
})

export default connect(mapStateToProps, mapDispatchToProps)(EmployeeDetailModal)
